import React, { useEffect, useState } from "react";
import { apiBaseUrl, imageBaseurl } from "../utils/common";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";

const BlogsPage = () => {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");

  // Initial API Call
  useEffect(() => {
    fetchBlogs(1, true);
  }, []);

  const fetchBlogs = async (pageNumber, isInitial = false) => {
    try {
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setLoading(true);
      }

      setError("");

      const res = await fetch(
        `${apiBaseUrl}/public/blogs?page=${pageNumber}&type=blog`
      );

      if (!res.ok) {
        throw new Error("Failed to load blogs");
      }

      const result = await res.json();

      const newPosts = result?.data?.data || [];
      const total = result?.data?.totalPages || 1;

      setTotalPages(total);

      if (pageNumber === 1) {
        setPosts(newPosts);
      } else {
        setPosts((prev) => [...prev, ...newPosts]);
      }

      setPage(pageNumber + 1);

    } catch (err) {
      setError("Something went wrong while loading blogs.");
      console.log(err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const loadMore = () => {
    fetchBlogs(page);
  };

  const hasMore = page <= totalPages;

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4">

        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Latest News
          </h2>
        </div>

        {/* Initial Loader */}
        {initialLoading && (
          <p className="text-center">Loading blogs...</p>
        )}

        {/* No Data */}
        {!initialLoading && posts.length === 0 && (
          <p className="text-center text-gray-500">
            No news available
          </p>
        )}

        {/* Blog Grid */}

        <div className="grid md:grid-cols-3 gap-8">

          {posts.map((blog) => (
            <Link
              key={blog.postId}
              to={`/news/${blog.slug}`}
              className="border rounded-xl overflow-hidden shadow hover:shadow-lg transition duration-300 bg-white"
            >

              {/* Blog Image */}
              {blog.image && (
                <img
                  src={`${imageBaseurl}${blog.image}`}
                  alt={blog.title}
                  className="w-full h-48 object-cover"
                />
              )}

              {/* Blog Content */}
              <div className="p-4">

                {/* Date + Like */}
                <div className="flex items-center justify-between mb-3">

                  <p className="text-sm text-gray-500">
                    {new Date(blog.createdAt).toDateString()}
                  </p>

                  {/* Like Count */}
                  <div className="flex items-center gap-1 text-red-500 text-sm font-medium">
                    <Heart className="w-4 h-4 fill-red-500" />

                    <span>
                      {blog.liked || 0}
                    </span>
                  </div>

                </div>

                <h2 className="text-lg font-semibold line-clamp-2">
                  {blog.title}
                </h2>

              </div>

            </Link>
          ))}

        </div>

        {/* Error */}
        {error && (
          <p className="text-red-500 text-center mt-4">
            {error}
          </p>
        )}

        {/* Load More Button */}
        {hasMore && !initialLoading && (
          <div className="text-center mt-10">

            <button
              onClick={loadMore}
              disabled={loading}
              className="px-6 py-3 bg-black text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "Loading..." : "Load More"}
            </button>

          </div>
        )}

      </div>
    </section>
  );
};

export default BlogsPage;